import React, { useContext } from "react";

import COLORS from "shared/config/colors";
import EmptyState from "shared/components/data-display/tables/EmptyState";
import HStack from "shared/components/layout/stack/HStack";
import Loading from "shared/components/feedback/loading/Loading";
import SPACING from "shared/config/spacing";
import SessionContext from "app/contexts/SessionContext";
import SummaryTimeOffCard from "features/timesheet/time-off/summary-time-off/SummaryTimeOffCard";
import Text from "shared/components/data-display/text/Text";
import VStack from "shared/components/layout/stack/VStack";
import styled from "styled-components/macro";
import useTimeOffApi from "features/timesheet/time-off/querys/useTimeOff";

export const LoadingWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-content: center;
    background-color: white;
`;

const StyledHStack = styled(HStack)`
    gap: 0.8rem;
`;

const getCurrentYear = () => new Date().getFullYear();
const getNextYear = () => new Date().getFullYear() + 1;

const SummaryTimeOff = () => {
    const title = "Usage";
    const session = useContext(SessionContext);

    const { useTimeOffSummaryCards } = useTimeOffApi();

    const timeOffSummary = useTimeOffSummaryCards({
        employeeId: session.legacyId,
        year: getCurrentYear(),
    });

    const nextYearsTimeOffSummary = useTimeOffSummaryCards({
        employeeId: session.legacyId,
        year: getNextYear(),
    });

    if (timeOffSummary.isError || nextYearsTimeOffSummary.isError) {
        return <>Error</>;
    }

    const nextYearSummaryExist =
        nextYearsTimeOffSummary?.data?.length > 0 &&
        !nextYearsTimeOffSummary?.data?.every((s) => s.allotment == null);

    const renderSummaryCards = (data) => {
        return (
            <StyledHStack wrap={true}>
                {data.map((timeOff) => (
                    <SummaryTimeOffCard
                        isSmallSize={nextYearSummaryExist}
                        remainingTime={timeOff.daysRemaining}
                        totalTime={timeOff.allotment}
                        title={timeOff.reasonName}
                        key={timeOff.reasonName}
                    />
                ))}
            </StyledHStack>
        );
    };

    return (
        <>
            <VStack>
                <Text variant={"Headline_4"} color={COLORS.Gray_1}>
                    {title}
                </Text>
            </VStack>
            <VStack>
                {(() => {
                    if (timeOffSummary.isLoading || nextYearsTimeOffSummary.isLoading) {
                        return (
                            <LoadingWrapper>
                                <Loading text="Loading summary..." />
                            </LoadingWrapper>
                        );
                    }
                    if (!timeOffSummary?.data || timeOffSummary?.data?.length === 0) {
                        return <EmptyState message="There are not allotments loaded for current year" />;
                    }
                    return (
                        <VStack spacing={SPACING.Zero_5}>
                            {nextYearSummaryExist && (
                                <Text variant={"Headline_5"} color={COLORS.Gray_1}>
                                    {getCurrentYear()}
                                </Text>
                            )}
                            {renderSummaryCards(timeOffSummary.data)}
                        </VStack>
                    );
                })()}

                {!timeOffSummary.isLoading &&
                    !nextYearsTimeOffSummary.isLoading &&
                    nextYearSummaryExist && (
                        <VStack spacing={SPACING.Zero_5}>
                            <Text variant={"Headline_5"} color={COLORS.Gray_1}>
                                {getNextYear()}
                            </Text>
                            {renderSummaryCards(nextYearsTimeOffSummary.data)}
                        </VStack>
                    )}
            </VStack>
        </>
    );
};

export default SummaryTimeOff;
